import React, { useState, useRef, useEffect } from 'react';

interface EditableCellProps {
  value: string;
  onSave: (newValue: string) => Promise<void>;
  type?: 'text' | 'email' | 'select';
  options?: string[]; // For select type
  placeholder?: string;
}

/**
 * EditableCell component that allows inline editing with optimistic updates
 */
const EditableCell: React.FC<EditableCellProps> = ({
  value,
  onSave,
  type = 'text',
  options = [],
  placeholder
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [isSaving, setIsSaving] = useState(false);
  const [hasError, setHasError] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement>(null);

  // Update edit value when prop value changes (for real-time updates)
  useEffect(() => {
    if (!isEditing) {
      setEditValue(value);
    }
  }, [value, isEditing]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current instanceof HTMLInputElement) {
        inputRef.current.select();
      }
    }
  }, [isEditing]);

  const handleStartEdit = () => {
    setIsEditing(true);
    setHasError(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditValue(value); // Reset to original value
    setHasError(false);
  };

  const handleSaveEdit = async () => {
    // Don't save if value hasn't changed
    if (editValue.trim() === value) {
      setIsEditing(false);
      return;
    }

    // Basic validation
    if (editValue.trim() === '') {
      setHasError(true);
      return;
    }

    // Email validation for email fields
    if (type === 'email' && editValue) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(editValue)) {
        setHasError(true);
        return;
      }
    }

    try {
      setIsSaving(true);
      setHasError(false);
      
      await onSave(editValue.trim());
      setIsEditing(false);
      
    } catch (error) {
      setHasError(true);
      console.error('Failed to save edit:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handleBlur = () => {
    // Small delay to allow clicking on other elements
    setTimeout(() => {
      if (isEditing) {
        handleSaveEdit();
      }
    }, 100);
  };

  // Display mode
  if (!isEditing) {
    return (
      <div
        className={`editable-cell ${hasError ? 'error' : ''}`}
        onClick={handleStartEdit}
        title="Click to edit"
      >
        {value || <span className="placeholder">{placeholder || 'Click to edit'}</span>}
        {isSaving && <span className="saving-indicator">💾</span>}
      </div>
    );
  }

  // Edit mode - select dropdown
  if (type === 'select') {
    return (
      <select
        ref={inputRef as React.RefObject<HTMLSelectElement>}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className={`editable-input ${hasError ? 'error' : ''}`}
        disabled={isSaving}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }

  // Edit mode - text/email input
  return (
    <input
      ref={inputRef as React.RefObject<HTMLInputElement>}
      type={type}
      value={editValue}
      onChange={(e) => setEditValue(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={`editable-input ${hasError ? 'error' : ''}`}
      disabled={isSaving}
      placeholder={placeholder}
    />
  );
};

export default EditableCell;